import { formatRomeDateTime } from "../../utils/datetime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import apiService from "../../services/api.service";
import { handleChatKeyDown } from "../../utils/chatKeyboard";
import "./dashboardEventChats.css";

const REFRESH_MS = 3000;

const DashboardEventChats = () => {
  const [chats, setChats] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [error, setError] = useState("");
  const [pending, setPending] = useState({});
  const [exporting, setExporting] = useState(false);
  const messagesRef = useRef(null);
  const selectionRef = useRef(null);
  const detailRequest = useRef(0);
  const detailInFlight = useRef(null);
  const readGeneration = useRef({});
  const listRequest = useRef(0);
  const pendingRef = useRef(new Set());
  const body = drafts[selectedId] || "";
  const busy = !!pending[selectedId];

  const loadList = useCallback(async () => {
    const request = ++listRequest.current;
    try {
      const response = await apiService.getEventChats();
      const list = response.data || [];
      if (request === listRequest.current) setChats(list);
    } catch {
      setError("Impossibile caricare le chat evento.");
    }
  }, []);

  const loadSelected = useCallback(async (id, markRead = false) => {
    if (!id) return;
    // Polling never supersedes a pending fetch; explicit clicks may supersede it.
    if (!markRead && detailInFlight.current !== null) return;
    const request = ++detailRequest.current;
    detailInFlight.current = request;
    const acknowledgement = markRead ? (readGeneration.current[id] || 0) + 1 : null;
    if (markRead) readGeneration.current[id] = acknowledgement;
    const isCurrent = () => selectionRef.current === id && request === detailRequest.current;
    try {
      const response = await apiService.getEventChat(id);
      if (!isCurrent()) return;
      setSelected(response.data);
      setError("");
      if (markRead) {
        // Only acknowledge the user messages included in the detail just displayed.
        const lastMessageId = response.data.latest_user_message_id || 0;
        ++listRequest.current;
        const readResponse = await apiService.markEventChatRead(id, lastMessageId);
        if (readGeneration.current[id] !== acknowledgement) return;
        ++listRequest.current;
        setChats(current => current.map(chat => chat.id === id ? {
          ...chat,
          has_unread: readResponse.data.has_unread || chat.latest_user_message_id > lastMessageId,
        } : chat));
      }
    } catch {
      if (isCurrent()) setError("Impossibile aggiornare la conversazione.");
    } finally {
      if (detailInFlight.current === request) detailInFlight.current = null;
    }
  }, []);

  const openChat = (id) => {
    selectionRef.current = id;
    setSelectedId(id);
    setSelected(null);
    loadSelected(id, true);
  };

  useEffect(() => {
    loadList();
    const interval = setInterval(loadList, REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadList]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!pendingRef.current.has(selectedId)) loadSelected(selectedId);
    }, REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadSelected, selectedId]);

  useEffect(() => () => {
    selectionRef.current = null;
    ++detailRequest.current;
    ++listRequest.current;
  }, []);

  useEffect(() => {
    const element = messagesRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [selected?.messages?.length]);

  const reply = async (event) => {
    event.preventDefault();
    const message = body.trim();
    const id = selectedId;
    if (!message || !id || pendingRef.current.has(id)) return;
    pendingRef.current.add(id);
    setPending(current => ({ ...current, [id]: true }));
    const request = ++detailRequest.current;
    try {
      const response = await apiService.replyEventChat(id, message);
      if (selectionRef.current === id && request === detailRequest.current) setSelected(response.data);
      setDrafts(current => current[id] === body ? { ...current, [id]: "" } : current);
      await loadList();
    } catch (err) {
      if (selectionRef.current === id) setError(err.response?.data?.detail || "Invio non riuscito.");
    } finally {
      pendingRef.current.delete(id);
      setPending(current => ({ ...current, [id]: false }));
    }
  };

  const closeChat = async () => {
    const id = selectedId;
    if (!id || pendingRef.current.has(id)) return;
    pendingRef.current.add(id);
    setPending(current => ({ ...current, [id]: true }));
    const request = ++detailRequest.current;
    try {
      const response = await apiService.closeEventChat(id);
      if (selectionRef.current === id && request === detailRequest.current) setSelected(response.data);
      await loadList();
    } catch (err) {
      if (selectionRef.current === id) setError(err.response?.data?.detail || "Chiusura chat non riuscita.");
    } finally {
      pendingRef.current.delete(id);
      setPending(current => ({ ...current, [id]: false }));
    }
  };

  const exportChats = async () => {
    if (exporting || !selected?.event_finished) return;
    setExporting(true);
    try {
      await apiService.downloadEventChats(selected.event_id);
    } catch (err) {
      setError(err.response?.data?.detail || "Esportazione chat non riuscita.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="dashboard-card dashboard-event-chats">
      <div className="dashboard-card__header">
        <div>
          <h1 className="dashboard-card__title">Chat evento</h1>
          <p className="dashboard-card__subtitle">Conversazioni con gli utenti, ordinate dalla più recente.</p>
        </div>
        <span className="dashboard-count">{chats.length} chat</span>
      </div>
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      {!chats.length ? (
        <Alert variant="info">Nessun utente ha ancora aperto una chat.</Alert>
      ) : (
        <div className="dashboard-event-chats__layout">
          <div className="dashboard-event-chats__list" role="list" aria-label="Conversazioni">
            {chats.map((chat) => (
              <button
                type="button"
                role="listitem"
                key={chat.id}
                className={`dashboard-event-chats__list-item${selectedId === chat.id ? " is-active" : ""}`}
                onClick={() => openChat(chat.id)}
              >
                <span className="dashboard-event-chats__list-top">
                  <strong>{chat.user_name}</strong>
                  {chat.has_unread && <span className="dashboard-event-chats__unread" role="img" aria-label="Messaggi non letti" />}
                  <small>{chat.updated_at ? formatRomeDateTime(chat.updated_at, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : ""}</small>
                </span>
                <span>{chat.user_email}</span>
                <span>{chat.event_name}</span>
                <em className={chat.can_write ? "is-open" : ""}>{chat.can_write ? "Aperta" : "Terminata"}</em>
              </button>
            ))}
          </div>
          <section className="dashboard-event-chats__conversation" aria-label="Conversazione selezionata">
            {selected && (
              <>
                <header className="dashboard-event-chats__conversation-header">
                  <div>
                    <h2>{selected.user_name}</h2>
                    <p>{selected.user_email} · {selected.event_name}</p>
                  </div>
                  {selected.event_finished && (
                    <Button variant="outline-primary" size="sm" disabled={exporting} onClick={exportChats}>
                      Esporta chat evento (Word)
                    </Button>
                  )}
                  {!selected.is_closed && selected.event_active && (
                    <Button variant="outline-danger" size="sm" disabled={busy} onClick={closeChat}>
                      Termina chat
                    </Button>
                  )}
                </header>
                <div className="dashboard-event-chats__messages" ref={messagesRef} aria-live="polite">
                  {selected.messages.map((message) => (
                    <div
                      key={message.id}
                      className={`dashboard-event-chats__message${message.sender_role === "gestore" ? " is-manager" : ""}`}
                    >
                      <strong>{message.sender_role === "gestore" ? "Tu" : selected.user_name}</strong>
                      <p>{message.body}</p>
                      <time>{formatRomeDateTime(message.created_at)}</time>
                    </div>
                  ))}
                </div>
                {selected.can_write ? (
                  <Form className="dashboard-event-chats__reply" onSubmit={reply}>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={4000}
                      value={body}
                      onChange={(event) => setDrafts({ ...drafts, [selectedId]: event.target.value })}
                      onKeyDown={(event) => handleChatKeyDown(event, reply, busy)}
                      placeholder="Scrivi una risposta..."
                      aria-label="Risposta all'utente"
                    />
                    <Button type="submit" disabled={busy || !body.trim()}>{busy ? "Invio..." : "Invia"}</Button>
                  </Form>
                ) : (
                  <p className="dashboard-event-chats__closed">Conversazione terminata: non è più possibile inviare messaggi.</p>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default DashboardEventChats;
